// SEQ-180 phase 2, task P13: the chart bodies a user-created widget renders.
//
// The five phase 1 chart widgets keep their own components (the axis toggle,
// the trend project selector, the table's column visibility and exports are
// all behaviour the widget config has no field for). Everything a user builds
// in the drawer renders here, driven only by the config plus the registry's
// units, so any metric in the vocabulary works on any chart type the
// validator allows.
//
// SEQ-180 follow-up (14 September 2026), the client's own complaint. Every
// chart here passed Recharts a bare `formatter`, which is handed the value and
// the series name and NEVER the bucket. A widget grouped by project therefore
// showed a number and a metric name with no project attached, and the reader
// had to guess which bar they were on. All four chart types now share one
// tooltip that leads with the bucket, says how the widget is grouped, and
// formats each series by its own unit (widgetChartLabels.ts).
import { Fragment, useMemo, type ReactElement } from "react";
import { motion, useReducedMotion } from "framer-motion";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from "recharts";

import type { PnlWidgetConfig } from "../../lib/pnl/pnlWidgetSchema";
import type { PnlUnit } from "../../lib/pnl/pnlAnalytics";
import { formatMetricValue } from "../../lib/pnl/formatMetricValue";
import { formatPnlMetric } from "../../features/pnlAnalytics/pnlAnalyticsFormat";
import { axisTickFormatter, type WidgetBucket } from "../../features/pnlAnalytics/widgetBuckets";
import {
  buildWidgetTooltipModel,
  categoryChartMinWidth,
  resolveSeriesUnit,
  truncateAxisLabel,
  type WidgetSeriesKey,
} from "../../features/pnlAnalytics/widgetChartLabels";
import { groupColumnHeading } from "../../features/pnlAnalytics/groupByVocabulary";
import { chartAnimation, tooltipVariants } from "../../features/pnlAnalytics/pnlMotion";
import { pnlLegendLabel } from "./pnlLegendLabel";
import "./PnlWidgetCharts.css";

// Design audit, MEDIUM: these were literal hexes that happened to match the
// tokens. Reading the tokens means a brand change reaches these charts, and
// means nobody has to know that #1e73be was the brand blue.
//
// The comparison series was --text-muted (2.56:1) and is now
// --text-secondary: a dashed prior-period line has to be legible against the
// grid, not a suggestion of one.
const SERIES_COLOUR = "var(--seq-blue)";
const SECONDARY_COLOUR = "var(--color-warning)";
const COMPARISON_COLOUR = "var(--text-secondary)";
/** A negative bar. A chart fill, so the 3:1 non-text threshold applies and --color-error passes it. */
const NEGATIVE_COLOUR = "var(--color-error)";

const axisTick = axisTickFormatter;

interface GenericChartProps {
  config: PnlWidgetConfig;
  buckets: WidgetBucket[];
  unit: PnlUnit;
  secondaryUnit: PnlUnit;
  metricName: string;
  secondaryName: string;
  comparisonName: string | null;
  onBucketClick?: (bucket: WidgetBucket) => void;
}

/** The copy an empty widget shows. Pinned by widgetChartLabels.test.ts so it keeps saying what to do next. */
export const WIDGET_EMPTY_MESSAGE = "No data for this range. Try a wider date range, or clear a project filter.";

function EmptyBody() {
  return (
    <div className="pnl-widget-empty">
      <span>{WIDGET_EMPTY_MESSAGE}</span>
    </div>
  );
}

/**
 * A zero line is drawn only when something crosses it. On an all-positive
 * chart the axis already is the baseline and a second rule would be one more
 * line to read for no information (the chartjunk rule).
 */
function hasNegative(buckets: WidgetBucket[]): boolean {
  return buckets.some((b) => (b.value ?? 0) < 0 || (b.secondary ?? 0) < 0);
}

/**
 * Value labels are for a chart sparse enough to read them. Past six buckets
 * they collide with each other and the chart is better read by its shape, with
 * the tooltip for the number.
 */
const VALUE_LABEL_MAX_BUCKETS = 6;

/**
 * A bar's own label, in the axis's register rather than the tooltip's.
 *
 * "$40.0K" beside an axis reading "65k" is one number; "$40,000.00" beside it
 * is two ideas about the same figure and twice the width on a bar 60px wide.
 * The tooltip is where the exact figure lives. Non-money units keep their full
 * formatting, since "428.5 hrs" is already short and compacting hours would
 * lose the half hour that overtime is measured in.
 */
function barLabel(value: number | null, unit: PnlUnit): string {
  if (value === null) return "";
  if (unit === "currency" || unit === "currency_per_hour") return formatPnlMetric(value, "compact-currency");
  return formatMetricValue(value, unit);
}

/**
 * One entry of what Recharts hands a custom tooltip. Typed here rather than
 * imported, because Recharts' own payload type is generic over value and name
 * types this page never varies, and because `payload.payload` is our own
 * bucket, which is the whole reason the tooltip can name anything.
 */
interface TooltipPayloadEntry {
  dataKey?: string | number;
  name?: string | number;
  value?: number | string | null;
  color?: string;
  payload?: WidgetBucket;
}

interface WidgetTooltipProps {
  /** Injected by Recharts through `content`. */
  active?: boolean;
  /** Injected by Recharts through `content`. */
  payload?: TooltipPayloadEntry[];
  groupBy: string;
  unit: PnlUnit;
  secondaryUnit: PnlUnit;
}

function seriesKeyOf(dataKey: string | number | undefined): WidgetSeriesKey {
  if (dataKey === "secondary") return "secondary";
  if (dataKey === "comparison") return "comparison";
  return "value";
}

function fallbackColour(key: WidgetSeriesKey): string {
  if (key === "secondary") return SECONDARY_COLOUR;
  if (key === "comparison") return COMPARISON_COLOUR;
  return SERIES_COLOUR;
}

/**
 * The tooltip every generic chart shows. Bucket first, grouping second, then
 * one row per series formatted by that series' own unit, so an hours metric
 * never picks up a dollar sign from the dollars beside it.
 */
function WidgetTooltip({ active, payload, groupBy, unit, secondaryUnit }: WidgetTooltipProps) {
  const reduce = useReducedMotion() ?? false;
  if (!active || !payload || payload.length === 0) return null;

  const model = buildWidgetTooltipModel(
    groupBy,
    payload[0]?.payload?.label,
    payload.map((entry) => {
      const key = seriesKeyOf(entry.dataKey);
      return {
        name: String(entry.name ?? ""),
        value: typeof entry.value === "number" ? entry.value : null,
        unit: resolveSeriesUnit(key, unit, secondaryUnit),
        colour: entry.color ?? fallbackColour(key),
      };
    }),
  );

  return (
    <motion.div
      key={model.title}
      className="pnl-tooltip"
      role="tooltip"
      variants={tooltipVariants(reduce)}
      initial="hidden"
      animate="visible"
    >
      <div className="pnl-tooltip-title">{model.title}</div>
      <div className="pnl-tooltip-grouping">{model.grouping}</div>
      <div className="pnl-tooltip-rows">
        {/* Keyed by position, not by content: two series can share a name and
            a formatted value, and the everyday case is both reading "N/A"
            against a period with no data. */}
        {model.rows.map((row, index) => (
          <Fragment key={`${index}-${row.name}`}>
            <span className="pnl-tooltip-swatch" style={{ background: row.colour }} aria-hidden="true" />
            <span className="pnl-tooltip-name">{row.name}</span>
            <span className="pnl-tooltip-value">{row.value}</span>
          </Fragment>
        ))}
      </div>
    </motion.div>
  );
}

/**
 * A category chart's body: height from the stylesheet, and a minimum drawing
 * width of one readable column per bucket. Twenty projects in a quarter-width
 * card scroll sideways rather than drawing twenty hairlines, and on a phone
 * every chart of any size does.
 */
function CategoryChartFrame({ count, children }: { count: number; children: ReactElement }) {
  return (
    <div className="pnl-widget-chart-scroll">
      <div className="pnl-widget-chart" style={{ minWidth: categoryChartMinWidth(count) }}>
        <ResponsiveContainer>{children}</ResponsiveContainer>
      </div>
    </div>
  );
}

/** Shortens a long project or builder name so ticks stay legible. The tooltip carries the full name. */
const categoryTick = (value: string) => truncateAxisLabel(String(value));

export function PnlWidgetBarChart({
  config,
  buckets,
  unit,
  secondaryUnit,
  metricName,
  comparisonName,
  onBucketClick,
}: GenericChartProps) {
  const reduce = useReducedMotion() ?? false;
  if (buckets.length === 0) return <EmptyBody />;

  return (
    <CategoryChartFrame count={buckets.length}>
      <BarChart data={buckets} margin={{ top: 14, right: 20, left: 0, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
        <XAxis
          dataKey="label"
          tickFormatter={categoryTick}
          tick={{ fontSize: 12, fill: "var(--text-secondary)" }}
          interval="preserveStartEnd"
          minTickGap={8}
        />
        <YAxis tickFormatter={axisTick(unit)} tick={{ fontSize: 12, fill: "var(--text-secondary)" }} width={52} />
        <Tooltip
          content={(<WidgetTooltip groupBy={config.groupBy} unit={unit} secondaryUnit={secondaryUnit} />) as never}
          cursor={{ fill: "rgba(0,0,0,0.04)" }}
        />
        {/* Money below zero needs a line to be below. Drawn only when a bucket
            actually crosses it. */}
        {hasNegative(buckets) && <ReferenceLine y={0} stroke="var(--text-secondary)" strokeWidth={1} />}
        {comparisonName && <Legend wrapperStyle={{ fontSize: 12 }} formatter={pnlLegendLabel as never} />}
        <Bar
          dataKey="value"
          name={metricName}
          radius={[6, 6, 0, 0]}
          maxBarSize={72}
          activeBar={{ fillOpacity: 0.82 }}
          cursor={onBucketClick ? "pointer" : undefined}
          onClick={(_data: unknown, index: number) => {
            const bucket = buckets[index];
            if (bucket) onBucketClick?.(bucket);
          }}
          {...chartAnimation(reduce, 0)}
        >
          {buckets.map((bucket) => (
            <Cell key={bucket.key} fill={(bucket.value ?? 0) >= 0 ? SERIES_COLOUR : NEGATIVE_COLOUR} />
          ))}
          {buckets.length <= VALUE_LABEL_MAX_BUCKETS && !comparisonName && (
            <LabelList
              dataKey="value"
              position="top"
              fontSize={12}
              fill="var(--text-secondary)"
              formatter={((v: number | null) => barLabel(v, unit)) as never}
            />
          )}
        </Bar>
        {comparisonName && (
          <Bar
            dataKey="comparison"
            name={comparisonName}
            radius={[6, 6, 0, 0]}
            maxBarSize={72}
            fill={COMPARISON_COLOUR}
            activeBar={{ fillOpacity: 0.82 }}
            {...chartAnimation(reduce, 1)}
          />
        )}
      </BarChart>
    </CategoryChartFrame>
  );
}

export function PnlWidgetStackedBarChart({
  buckets,
  unit,
  secondaryUnit,
  metricName,
  secondaryName,
  config,
  onBucketClick,
}: GenericChartProps) {
  const reduce = useReducedMotion() ?? false;
  if (buckets.length === 0) return <EmptyBody />;

  return (
    <CategoryChartFrame count={buckets.length}>
      <BarChart data={buckets} margin={{ top: 14, right: 20, left: 0, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
        <XAxis
          dataKey="label"
          tickFormatter={categoryTick}
          tick={{ fontSize: 12, fill: "var(--text-secondary)" }}
          interval="preserveStartEnd"
          minTickGap={8}
        />
        <YAxis tickFormatter={axisTick(unit)} tick={{ fontSize: 12, fill: "var(--text-secondary)" }} width={52} />
        <Tooltip
          content={(<WidgetTooltip groupBy={config.groupBy} unit={unit} secondaryUnit={secondaryUnit} />) as never}
          cursor={{ fill: "rgba(0,0,0,0.04)" }}
        />
        {hasNegative(buckets) && <ReferenceLine y={0} stroke="var(--text-secondary)" strokeWidth={1} />}
        <Legend wrapperStyle={{ fontSize: 12 }} formatter={pnlLegendLabel as never} />
        <Bar
          dataKey="value"
          stackId="widget"
          name={metricName}
          fill={SERIES_COLOUR}
          maxBarSize={72}
          activeBar={{ fillOpacity: 0.82 }}
          cursor={onBucketClick ? "pointer" : undefined}
          onClick={(_data: unknown, index: number) => {
            // Recharts hands back an index into the data it was given. A click
            // landing while the data is being replaced would otherwise drill
            // into undefined.
            const bucket = buckets[index];
            if (bucket) onBucketClick?.(bucket);
          }}
          {...chartAnimation(reduce, 0)}
        />
        {config.secondaryMetric && (
          <Bar
            dataKey="secondary"
            stackId="widget"
            name={secondaryName}
            fill={SECONDARY_COLOUR}
            maxBarSize={72}
            activeBar={{ fillOpacity: 0.82 }}
            {...chartAnimation(reduce, 1)}
          />
        )}
      </BarChart>
    </CategoryChartFrame>
  );
}

export function PnlWidgetLineChart({
  buckets,
  unit,
  secondaryUnit,
  metricName,
  secondaryName,
  comparisonName,
  config,
}: GenericChartProps) {
  const reduce = useReducedMotion() ?? false;
  if (buckets.length === 0) return <EmptyBody />;

  return (
    <CategoryChartFrame count={buckets.length}>
      <LineChart data={buckets} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
        <XAxis
          dataKey="label"
          tickFormatter={categoryTick}
          tick={{ fontSize: 12, fill: "var(--text-secondary)" }}
          interval="preserveStartEnd"
          minTickGap={8}
        />
        <YAxis tickFormatter={axisTick(unit)} tick={{ fontSize: 12, fill: "var(--text-secondary)" }} width={52} />
        <Tooltip content={(<WidgetTooltip groupBy={config.groupBy} unit={unit} secondaryUnit={secondaryUnit} />) as never} />
        {hasNegative(buckets) && <ReferenceLine y={0} stroke="var(--text-secondary)" strokeWidth={1} />}
        <Legend wrapperStyle={{ fontSize: 12 }} formatter={pnlLegendLabel as never} />
        {/* Recharts draws a line in by revealing its own path, which is the
            series arriving in the direction it is read. */}
        <Line
          type="monotone"
          dataKey="value"
          name={metricName}
          stroke={SERIES_COLOUR}
          strokeWidth={2}
          dot={{ r: 3 }}
          activeDot={{ r: 5, strokeWidth: 2 }}
          connectNulls
          {...chartAnimation(reduce, 0)}
        />
        {config.secondaryMetric && (
          <Line
            type="monotone"
            dataKey="secondary"
            name={secondaryName}
            stroke={SECONDARY_COLOUR}
            strokeWidth={2}
            dot={{ r: 3 }}
            activeDot={{ r: 5, strokeWidth: 2 }}
            connectNulls
            {...chartAnimation(reduce, 1)}
          />
        )}
        {comparisonName && (
          <Line
            type="monotone"
            dataKey="comparison"
            name={comparisonName}
            stroke={COMPARISON_COLOUR}
            strokeWidth={2}
            strokeDasharray="5 4"
            dot={false}
            connectNulls
            {...chartAnimation(reduce, 2)}
          />
        )}
      </LineChart>
    </CategoryChartFrame>
  );
}

export function PnlWidgetScatterChart({
  config,
  buckets,
  unit,
  secondaryUnit,
  metricName,
  secondaryName,
  onBucketClick,
}: GenericChartProps) {
  const reduce = useReducedMotion() ?? false;
  const points = useMemo(() => buckets.filter((b) => b.value !== null && b.secondary !== null), [buckets]);
  if (points.length === 0) return <EmptyBody />;

  return (
    <div className="pnl-widget-chart-scroll">
      <div className="pnl-widget-chart" data-chart="scatter">
        <ResponsiveContainer>
          <ScatterChart margin={{ top: 10, right: 20, left: 0, bottom: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
            <XAxis
              type="number"
              dataKey="value"
              name={metricName}
              tickFormatter={axisTick(unit)}
              tick={{ fontSize: 12, fill: "var(--text-secondary)" }}
              label={{ value: metricName, position: "insideBottom", offset: -12, fontSize: 12, fill: "var(--text-secondary)" }}
            />
            <YAxis
              type="number"
              dataKey="secondary"
              name={secondaryName}
              tickFormatter={axisTick(secondaryUnit)}
              tick={{ fontSize: 12, fill: "var(--text-secondary)" }}
              width={52}
              label={{ value: secondaryName, angle: -90, position: "insideLeft", fontSize: 12, fill: "var(--text-secondary)" }}
            />
            <ZAxis range={[80, 80]} />
            <Tooltip
              cursor={{ strokeDasharray: "3 3" }}
              content={(<WidgetTooltip groupBy={config.groupBy} unit={unit} secondaryUnit={secondaryUnit} />) as never}
            />
            <Scatter
              data={points}
              cursor={onBucketClick ? "pointer" : undefined}
              {...chartAnimation(reduce, 0)}
              onClick={(clicked: unknown) => {
                const candidate = clicked as { payload?: WidgetBucket } & Partial<WidgetBucket>;
                const bucket = candidate?.payload ?? (candidate as WidgetBucket | undefined);
                if (bucket) onBucketClick?.(bucket);
              }}
            >
              {points.map((point) => (
                <Cell key={point.key} fill={SERIES_COLOUR} />
              ))}
            </Scatter>
          </ScatterChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export function PnlWidgetTable({ buckets, unit, secondaryUnit, metricName, secondaryName, config, onBucketClick }: GenericChartProps) {
  if (buckets.length === 0) return <EmptyBody />;

  return (
    <div className="pnl-widget-table-scroll">
      <table className="pnl-widget-table">
        <thead>
          <tr>
            {/* This heading was the literal word "Group", which told the reader
                nothing about what the rows are. It now names the grouping. */}
            <th scope="col" data-align="left">
              {groupColumnHeading(config.groupBy)}
            </th>
            <th scope="col" data-align="right">
              {metricName}
            </th>
            {config.secondaryMetric && (
              <th scope="col" data-align="right">
                {secondaryName}
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {buckets.map((bucket) => (
            <tr key={bucket.key}>
              <td data-align="left">
                {onBucketClick ? (
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm pnl-widget-table-link"
                    style={{ padding: 0, background: "none", color: "var(--color-primary)", fontWeight: 600 }}
                    onClick={() => onBucketClick(bucket)}
                  >
                    {bucket.label}
                  </button>
                ) : (
                  bucket.label
                )}
              </td>
              <td data-align="right" data-seq-private>{formatMetricValue(bucket.value, unit)}</td>
              {config.secondaryMetric && <td data-align="right" data-seq-private>{formatMetricValue(bucket.secondary, secondaryUnit)}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
